import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles, alpha } from "@material-ui/core/styles";
import { red, blue } from "@material-ui/core/colors";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import TodayRoundedIcon from "@material-ui/icons/TodayRounded";
import RefreshRoundedIcon from "@material-ui/icons/RefreshRounded";
import moment from "moment";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import AgendamentoDetalhes from "../../components/AgendamentoDetalhes";
import AgendamentoModal from "../../components/AgendamentoModal";
import { isDarkTheme } from "../../helpers/darkTint";

const CLOSED_STATUSES = ["concluido", "cancelado"];

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    padding: theme.spacing(3),
    background: isDarkTheme(theme)
      ? theme.palette.background.default
      : "linear-gradient(180deg, #f6f9fc 0%, #edf3f8 100%)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  card: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
  },
  overdueCard: {
    border: `1px solid ${isDarkTheme(theme) ? alpha(red[500], 0.4) : "#ef9a9a"}`,
    backgroundColor: isDarkTheme(theme) ? alpha(red[500], 0.2) : "#ffebee",
  },
  todayCard: {
    border: `1px solid ${isDarkTheme(theme) ? alpha(blue[500], 0.4) : "#b3e5fc"}`,
    backgroundColor: isDarkTheme(theme) ? alpha(blue[500], 0.2) : "#e3f2fd",
  },
  sectionPaper: {
    borderRadius: 10,
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : "#d8e2ea"}`,
    marginBottom: theme.spacing(2),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : "#e8eef3"}`,
  },
  sectionBody: {
    padding: theme.spacing(1.5),
  },
  overdueItem: {
    border: `1px solid ${isDarkTheme(theme) ? alpha(red[500], 0.4) : "#ffcdd2"}`,
    backgroundColor: isDarkTheme(theme) ? alpha(red[500], 0.2) : "#fff5f5",
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    cursor: "pointer",
  },
  todayItem: {
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : "#cfd8dc"}`,
    backgroundColor: isDarkTheme(theme) ? theme.palette.action.hover : "#f9fbfd",
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    cursor: "pointer",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
}));

const formatDateTime = value => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "Sem data";
  return moment(date).format("DD/MM/YYYY HH:mm");
};

const isSameDay = (dateValue, referenceDate) => {
  const date = new Date(dateValue);
  return (
    date.getDate() === referenceDate.getDate() &&
    date.getMonth() === referenceDate.getMonth() &&
    date.getFullYear() === referenceDate.getFullYear()
  );
};

const FollowUpDashboard = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [todayFollowups, setTodayFollowups] = useState([]);
  const [overdueFollowups, setOverdueFollowups] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAgendamento, setSelectedAgendamento] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("userId");
      const { data: todayData } = await api.get("/agendamentos/hoje/list");
      const { data: allData } = await api.get("/agendamentos", {
        params: {
          tipo: "followup",
          userId,
          limit: 500,
        },
      });

      const now = new Date();
      const todayItems = (todayData || [])
        .filter(item => item.tipo === "followup")
        .sort((a, b) => new Date(a.dataInicio) - new Date(b.dataInicio));

      const overdueItems = (allData?.agendamentos || [])
        .filter(item => {
          if (!item?.dataInicio) return false;
          if (CLOSED_STATUSES.includes(item.status)) return false;
          const startDate = new Date(item.dataInicio);
          if (Number.isNaN(startDate.getTime())) return false;
          return startDate < now && !isSameDay(startDate, now);
        })
        .sort((a, b) => new Date(a.dataInicio) - new Date(b.dataInicio));

      setTodayFollowups(todayItems);
      setOverdueFollowups(overdueItems);
    } catch (error) {
      toastError(error);
      setTodayFollowups([]);
      setOverdueFollowups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenFollowup = async item => {
    try {
      const { data } = await api.get(`/agendamentos/${item.id}`);
      setSelectedAgendamento(data);
      setDetailsOpen(true);
    } catch (error) {
      toastError(error);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedAgendamento(null);
  };

  const handleOpenEdit = () => {
    setDetailsOpen(false);
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setSelectedAgendamento(null);
  };

  const handleSaveEdit = async () => {
    setEditModalOpen(false);
    setDetailsOpen(false);
    setSelectedAgendamento(null);
    await loadData();
  };

  const handleDeleteFollowup = async () => {
    setDetailsOpen(false);
    setSelectedAgendamento(null);
    await loadData();
  };

  const totalOpenFollowups = useMemo(() => {
    return overdueFollowups.length + todayFollowups.length;
  }, [overdueFollowups.length, todayFollowups.length]);

  const renderItem = (item, isOverdue = false) => {
    const contactName =
      item?.contato?.nome || item?.cliente?.NOMEFANTASIA || item?.cliente?.NOMECLI || "Sem contato";

    return (
      <ListItem
        key={item.id}
        button
        onClick={() => handleOpenFollowup(item)}
        className={isOverdue ? classes.overdueItem : classes.todayItem}
      >
        <ListItemText
          primary={item.titulo || "Follow-up sem título"}
          secondary={`${contactName} | ${formatDateTime(item.dataInicio)}`}
        />
        {isOverdue && <Chip label="Atrasado" size="small" style={{ backgroundColor: "#d32f2f", color: "#fff" }} />}
      </ListItem>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h5">Dashboard de Follow-ups</Typography>
          <Typography variant="body2" color="textSecondary">
            Visão rápida dos seus follow-ups do dia e pendências atrasadas.
          </Typography>
        </Box>
        <Button
          color="primary"
          variant="contained"
          startIcon={<RefreshRoundedIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Atualizar
        </Button>
      </Box>

      <Box className={classes.cards}>
        <Paper className={`${classes.card} ${classes.overdueCard}`}>
          <Typography variant="caption" color="textSecondary">Atrasados</Typography>
          <Typography variant="h4">{overdueFollowups.length}</Typography>
        </Paper>
        <Paper className={`${classes.card} ${classes.todayCard}`}>
          <Typography variant="caption" color="textSecondary">Hoje</Typography>
          <Typography variant="h4">{todayFollowups.length}</Typography>
        </Paper>
        <Paper className={classes.card}>
          <Typography variant="caption" color="textSecondary">Total em foco</Typography>
          <Typography variant="h4">{totalOpenFollowups}</Typography>
        </Paper>
      </Box>

      {loading ? (
        <Box display="flex" alignItems="center" gridGap={12}>
          <CircularProgress size={24} />
          <Typography>Carregando follow-ups...</Typography>
        </Box>
      ) : (
        <>
          <Paper className={classes.sectionPaper}>
            <Box className={classes.sectionHeader}>
              <WarningRoundedIcon style={{ color: "#c62828" }} />
              <Typography variant="h6">Follow-ups atrasados</Typography>
            </Box>
            <Box className={classes.sectionBody}>
              <List dense>
                {overdueFollowups.length > 0 ? overdueFollowups.map(item => renderItem(item, true)) : (
                  <ListItem>
                    <ListItemText primary="Nenhum follow-up atrasado." />
                  </ListItem>
                )}
              </List>
            </Box>
          </Paper>

          <Paper className={classes.sectionPaper}>
            <Box className={classes.sectionHeader}>
              <TodayRoundedIcon style={{ color: "#1565c0" }} />
              <Typography variant="h6">Follow-ups do dia</Typography>
            </Box>
            <Box className={classes.sectionBody}>
              <List dense>
                {todayFollowups.length > 0 ? todayFollowups.map(item => renderItem(item)) : (
                  <ListItem>
                    <ListItemText primary="Você não possui follow-ups para hoje." />
                  </ListItem>
                )}
              </List>
            </Box>
          </Paper>

          <Divider />
          <Box className={classes.actions}>
            <Button color="default" onClick={() => window.close()}>
              Fechar Janela
            </Button>
            <Button color="primary" variant="contained" onClick={() => (window.location.href = "/followup")}>
              Ir para Agenda Completa
            </Button>
          </Box>
        </>
      )}

      <AgendamentoDetalhes
        open={detailsOpen}
        onClose={handleCloseDetails}
        agendamento={selectedAgendamento}
        onEdit={handleOpenEdit}
        onDelete={handleDeleteFollowup}
        onConclude={async updatedAgendamento => {
          setSelectedAgendamento(updatedAgendamento);
          await loadData();
        }}
      />

      <AgendamentoModal
        open={editModalOpen}
        onClose={handleCloseEdit}
        agendamento={selectedAgendamento}
        forceFollowupType
        onSave={handleSaveEdit}
      />
    </Box>
  );
};

export default FollowUpDashboard;
